import api from '../../api';

export const GAISUKE_FX_QUOTE_POLL_MS = 30_000;

export interface GaisukeFxEquityPoint {
  date: string;
  equityJpy: number;
}

export interface GaisukeFxPolicyPublic {
  policyDate: string;
  slot: string;
  /** 方針を決めた時刻。指値はこれ以降の値動きだけで到達を判定する */
  decidedAt?: string | null;
  /** 指値の期限日。成行・見送りは決定直後に確定するので policyDate と同じ */
  executionDate?: string | null;
  /**
   * 採用指値の打ち切り枠。evening=executionDate 当日 20:00 / morning=executionDate 08:00。
   * 無い会議スロットは am→20:00・pm→08:00 で補う。
   */
  limitCutoff?: 'evening' | 'morning' | null;
  side: string;
  confidence: number;
  limitPrice: number | null;
  /** 保存時にロット丸め済みの希望建玉。null は見送り、または1ロットも取れないとき */
  targetNotionalUsd: number | null;
  reason: string;
  status: string;
  /** その社の API が支払い不足で方針を出せなかった */
  billingAttention?: boolean;
}

export interface GaisukeFxArmPublic {
  key: string;
  name: string;
  initialCashJpy: number;
  cashJpy: number;
  equityJpy: number;
  cumulativePnlJpy: number;
  pnlPct: number;
  liveMarked: boolean;
  /** 決済した回数。公開の売買履歴は直近分だけなので、回数と勝率は全履歴の集計 */
  closedTrades?: number;
  winningTrades?: number;
  position: { side: 'long' | 'short'; notionalUsd: number; avgRate: number } | null;
  equitySeries: GaisukeFxEquityPoint[];
  recentPolicies: GaisukeFxPolicyPublic[];
  joinsOn?: string | null;
  canTrade?: boolean;
  trades: Array<{
    tradeDate: string;
    executedAt: string | null;
    side: string;
    price: number;
    notionalUsd: number;
    fillStatus: string;
    /** 実現損益（円）。建玉は null、決済・スワップは金額 */
    pnlJpy: number | null;
  }>;
}

export interface GaisukeFxPublicStatus {
  nameJa: string;
  nameEn: string;
  disclaimerJa: string;
  disclaimerEn: string;
  symbol: string;
  startDate: string | null;
  endDate: string | null;
  status: string;
  weekdayElapsed: number;
  weekdayTotal: number;
  /** 何回目の実験か（開始日を1日目とする暦日とセットでバッジに出す） */
  roundNumber?: number | null;
  experimentDayNumber?: number | null;
  ended: boolean;
  quotePollMs: number;
  markRate: number | null;
  quoteAsOf: string | null;
  arms: GaisukeFxArmPublic[];
}

export interface GaisukeFxMarketCandle {
  ts: string;
  open: number;
  high: number;
  low: number;
  close: number;
}

export const GAISUKE_FX_CHART_TIMEFRAMES = ['15m', '1h', '4h', '1d'] as const;
export type GaisukeFxChartTimeframe = (typeof GAISUKE_FX_CHART_TIMEFRAMES)[number];

export const GAISUKE_FX_CHART_BAR_COUNTS = [60, 120, 240, 480] as const;
export type GaisukeFxChartBarCount = (typeof GAISUKE_FX_CHART_BAR_COUNTS)[number];
export const GAISUKE_FX_CHART_BAR_COUNT_DEFAULT: GaisukeFxChartBarCount = 120;

const CHART_BARS_STORAGE_KEY = 'gaisuke-fx-chart-bars';

export function readStoredFxChartBarCount(): GaisukeFxChartBarCount {
  try {
    const raw = localStorage.getItem(CHART_BARS_STORAGE_KEY);
    const n = Number(raw);
    if ((GAISUKE_FX_CHART_BAR_COUNTS as readonly number[]).includes(n)) {
      return n as GaisukeFxChartBarCount;
    }
  } catch {
    /* ignore */
  }
  return GAISUKE_FX_CHART_BAR_COUNT_DEFAULT;
}

export function writeStoredFxChartBarCount(bars: GaisukeFxChartBarCount): void {
  try {
    localStorage.setItem(CHART_BARS_STORAGE_KEY, String(bars));
  } catch {
    /* ignore */
  }
}

export interface GaisukeFxPublicMarket {
  symbol: string;
  pair: string;
  mid: number | null;
  bid: number | null;
  ask: number | null;
  spreadYen: number;
  spreadSen: number;
  previousClose: number | null;
  change: number | null;
  changePct: number | null;
  dayHigh: number | null;
  dayLow: number | null;
  asOf: string | null;
  timeframe: GaisukeFxChartTimeframe;
  timeframes?: GaisukeFxChartTimeframe[];
  barCount?: GaisukeFxChartBarCount;
  barCounts?: GaisukeFxChartBarCount[];
  fromDate?: string | null;
  candles: GaisukeFxMarketCandle[];
}

export interface GaisukeFxEaDownloadStats {
  arms: Array<{
    armKey: string;
    compiledMt5: number;
    compiledMt4: number;
    sourceMt5: number;
    sourceMt4: number;
  }>;
  shared: { coreMt5: number; coreMt4: number; magic: number; bundle: number };
  total: number;
}

export interface GaisukeFxEaSignalStats {
  today: string;
  requests: { today: number; total: number };
  allPolicies: { today: number; total: number };
  arms: Array<{ armKey: string; today: number; total: number }>;
}

export const gaisukeFxService = {
  async getStatus(): Promise<GaisukeFxPublicStatus> {
    const { data } = await api.get<GaisukeFxPublicStatus>('/gaisuke/fx/status');
    return data;
  },
  async getMarket(
    tf: GaisukeFxChartTimeframe = '1h',
    bars: GaisukeFxChartBarCount = GAISUKE_FX_CHART_BAR_COUNT_DEFAULT
  ): Promise<GaisukeFxPublicMarket> {
    const { data } = await api.get<GaisukeFxPublicMarket>('/gaisuke/fx/market', {
      params: { tf, bars },
    });
    return data;
  },
  async getEaDownloadStats(): Promise<GaisukeFxEaDownloadStats> {
    const { data } = await api.get<{ success: boolean; data: GaisukeFxEaDownloadStats }>(
      '/gaisuke/fx/ea/downloads/stats'
    );
    return data.data;
  },
  async getEaSignalStats(): Promise<GaisukeFxEaSignalStats> {
    const { data } = await api.get<{ success: boolean; data: GaisukeFxEaSignalStats }>(
      '/gaisuke/fx/ea/signals/stats'
    );
    return data.data;
  },
};
